/**
 * Financial Utilities
 * Handles transaction creation, validation, formatting, and balance updates
 */

export interface FinancialTransaction {
  id: string;
  accountId: string;
  type: 'deposit' | 'withdrawal' | 'transfer' | 'bill_payment' | 'interest' | 'fee';
  amount: number;
  direction: 'credit' | 'debit';
  description: string;
  status: 'completed' | 'pending' | 'failed' | 'approved' | 'rejected';
  createdAt: string;
  reference: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

// ─── Transaction Reference Generation ───────────────────────────────────────
export function generateTransactionReference(type: FinancialTransaction['type']): string {
  const typeMap: Record<FinancialTransaction['type'], string> = {
    deposit: 'DEP',
    withdrawal: 'WDR',
    transfer: 'TRF',
    bill_payment: 'BPL',
    interest: 'INT',
    fee: 'FEE',
  };

  const prefix = typeMap[type];
  const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const sequence = Math.floor(Math.random() * 9999)
    .toString()
    .padStart(4, '0');
  return `${prefix}-${date}-${sequence}`;
}

// ─── Realistic Transaction Amount Generation ───────────────────────────────
export function generateRealisticAmount(
  type: FinancialTransaction['type'],
  min = 10,
  max = 1999.99
): number {
  // Most transactions fall in typical range
  let amount: number;

  switch (type) {
    case 'bill_payment':
      // Bills typically 25-500
      amount = Math.random() * (500 - 25) + 25;
      break;
    case 'transfer':
      // Transfers vary widely, but keep realistic
      if (Math.random() < 0.7) {
        // 70% normal transfers (small-medium)
        amount = Math.random() * (999.99 - 50) + 50;
      } else {
        // 30% larger transfers
        amount = Math.random() * (max - 500) + 500;
      }
      break;
    case 'withdrawal':
      // Withdrawals typically 100-1500
      amount = Math.random() * (1500 - 100) + 100;
      break;
    case 'deposit':
      // Deposits typically 50-2000
      amount = Math.random() * (max - 50) + 50;
      break;
    case 'interest':
      // Interest small recurring amounts
      amount = Math.random() * (50 - 0.5) + 0.5;
      break;
    case 'fee':
      // Fees are usually small
      amount = Math.random() * (35 - 2.5) + 2.5;
      break;
    default:
      amount = Math.random() * (max - min) + min;
  }

  // Round to 2 decimal places
  return Math.round(amount * 100) / 100;
}

// ─── Transaction Creation ───────────────────────────────────────────────────
export function createTransaction(
  accountId: string,
  type: FinancialTransaction['type'],
  amount: number,
  description: string,
  status: 'completed' | 'pending' = 'completed'
): FinancialTransaction {
  const direction = ['credit', 'deposit', 'interest'].includes(type) ? 'credit' : 'debit';

  return {
    id: `txn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    accountId,
    type,
    amount,
    direction,
    description,
    status,
    createdAt: new Date().toISOString(),
    reference: generateTransactionReference(type),
  };
}

// ─── Validation ─────────────────────────────────────────────────────────────
export function validateAmount(amount: any): ValidationResult {
  const num = Number(amount);

  if (isNaN(num)) {
    return { isValid: false, error: 'Amount must be a valid number' };
  }

  if (num <= 0) {
    return { isValid: false, error: 'Amount must be greater than zero' };
  }

  if (!Number.isFinite(num)) {
    return { isValid: false, error: 'Amount is not a finite number' };
  }

  return { isValid: true };
}

export function validateBalance(currentBalance: number, amount: number): ValidationResult {
  if (currentBalance < amount) {
    return { isValid: false, error: `Insufficient balance. Available: $${formatCurrency(currentBalance)}` };
  }

  return { isValid: true };
}

export function validateTransaction(
  transaction: Partial<FinancialTransaction>
): ValidationResult {
  if (!transaction.type) {
    return { isValid: false, error: 'Transaction type is required' };
  }

  if (transaction.amount === undefined) {
    return { isValid: false, error: 'Amount is required' };
  }

  const amountValidation = validateAmount(transaction.amount);
  if (!amountValidation.isValid) {
    return amountValidation;
  }

  if (!transaction.description) {
    return { isValid: false, error: 'Description is required' };
  }

  return { isValid: true };
}

// ─── Currency Formatting ────────────────────────────────────────────────────
export function formatCurrency(amount: number, format: 'full' | 'compact' = 'full'): string {
  if (format === 'compact') {
    if (amount >= 1_000_000_000) {
      return `$${(amount / 1_000_000_000).toFixed(1)}B`;
    }
    if (amount >= 1_000_000) {
      return `$${(amount / 1_000_000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

// ─── Balance Updates ─────────────────────────────────────────────────────────
export function applyTransaction(
  currentBalance: number,
  transaction: FinancialTransaction
): number {
  if (transaction.direction === 'credit') {
    return currentBalance + transaction.amount;
  } else {
    return Math.max(0, currentBalance - transaction.amount);
  }
}

export function calculateBalance(
  initialBalance: number,
  transactions: FinancialTransaction[]
): number {
  return transactions.reduce((balance, txn) => applyTransaction(balance, txn), initialBalance);
}

// ─── Transaction Descriptions ───────────────────────────────────────────────
export function generateTransactionDescription(
  type: FinancialTransaction['type'],
  metadata?: { recipient?: string; account?: string; category?: string }
): string {
  const descriptions: Record<FinancialTransaction['type'], string[]> = {
    deposit: [
      'Account deposit',
      'Fund transfer in',
      'Direct deposit',
      'External transfer received',
    ],
    withdrawal: [
      'Account withdrawal',
      'Fund transfer out',
      'Cash withdrawal',
      'Transfer to external account',
    ],
    transfer: [
      metadata?.recipient ? `Transfer to ${metadata.recipient}` : 'Internal transfer',
      'Account-to-account transfer',
      'Inter-account transfer',
    ],
    bill_payment: [
      metadata?.category ? `${metadata.category} bill payment` : 'Bill payment',
      'Utilities payment',
      'Insurance payment',
      'Service payment',
    ],
    interest: [
      'Interest payment',
      'Account interest earned',
    ],
    fee: [
      'Account maintenance fee',
      'Monthly service fee',
      'Transaction fee',
      'Platform fee',
    ],
  };

  const descriptions_ = descriptions[type];
  return descriptions_[Math.floor(Math.random() * descriptions_.length)];
}

// ─── Transaction Filtering & Analysis ───────────────────────────────────────
export function filterTransactionsByType(
  transactions: FinancialTransaction[],
  type: FinancialTransaction['type'] | 'all' = 'all'
): FinancialTransaction[] {
  if (type === 'all') return transactions;
  return transactions.filter((t) => t.type === type);
}

export function filterTransactionsByDateRange(
  transactions: FinancialTransaction[],
  startDate: Date,
  endDate: Date
): FinancialTransaction[] {
  return transactions.filter((t) => {
    const txnDate = new Date(t.createdAt);
    return txnDate >= startDate && txnDate <= endDate;
  });
}

export function calculateTotalByType(
  transactions: FinancialTransaction[],
  type: FinancialTransaction['type']
): number {
  return transactions
    .filter((t) => t.type === type)
    .reduce((sum, t) => sum + t.amount, 0);
}
